import _ from "lodash";
import moment from "moment";
import { useState } from "react";
import { PrimaryButton } from "../components/Buttons";
import { DateInput } from "../components/Inputs";
import { PageHeader } from "../components/Layouts";
import { PrimaryTable, StaticFilterSection } from "../components/Tables";
import { useAlert, useApi, useReportDownloadHelpers } from "../hooks";
import constants from "../utils/constants";
import dateHelpers from "../utils/dateHelpers";

const ReportTable = ({ filters, data }) => {
  return (
    <PrimaryTable
      totalCount={data?.totalCount}
      displayCount={constants.DISPLAY_PER_PAGE}
      header={
        <PrimaryTable.Header>
          <PrimaryTable.HeaderColumn display="Menu Item Name" />
          <PrimaryTable.HeaderColumn display="Occurrences Count" />
        </PrimaryTable.Header>
      }
    >
      {data &&
        _.map(data, (r, index) => (
          <PrimaryTable.Row key={`${r.customMenuItemName}-${index}`} index={index}>
            <PrimaryTable.Cell>{r.customMenuItemName}</PrimaryTable.Cell>
            <PrimaryTable.Cell>{r.itemCount}</PrimaryTable.Cell>
          </PrimaryTable.Row>
        ))}
    </PrimaryTable>
  );
};

const ReportFilter = ({ setFilters, filters, onFiltersChange }) => {
  return (
    <StaticFilterSection filters={filters} showFilterCount={false}>
      <div className="grid grid-cols-12 gap-4 w-full p-4">
        <div className="col-span-2 min-w-full">
          <DateInput
            label="Date From"
            placeholder="Date From"
            value={filters.DateFrom}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                DateFrom: e.target.value,
              })
            }
          />
        </div>
        <div className="col-span-2 min-w-full">
          <DateInput
            label="Date Through"
            placeholder="Date Through"
            value={filters.DateThrough}
            required
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                DateThrough: e.target.value,
              })
            }
          />
        </div>
        <div className="col-span-8 min-w-full">
          <PrimaryButton 
            text="Reset"
            backgroundColor="gray-500"
            className="float-right mt-5"
            small
            onClick={() => setFilters({})}                    
          />
        </div>
      </div>
    </StaticFilterSection>
  );
};

export default function CustomMenuItemsReport() {
  const { downloadFileFromApiPost } = useReportDownloadHelpers();
  const { fetch, post } = useApi();
  const alert = useAlert();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tainted, setTainted] = useState(true);
  const [filters, setFilters] = useState({
    DateFrom: moment().subtract(1, "month").format(dateHelpers.YMD),
    DateThrough: moment().format(dateHelpers.YMD),
  });

  const onFiltersChange = (updatedFilters) => {
    setTainted(true);
    setFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
  };

  function getValidationWarnings() {
    let warnings = [];
    // no required fields
    return warnings;
  }

  function validate() {
    const warnings = getValidationWarnings();
    if (warnings.length) {
      alert(warnings.join(", "), "Please try again", "error");
      return false;
    }
    return true;
  }

  function buildPayload() {
    return {
      DateFrom: filters.DateFrom ? filters.DateFrom : null,
      DateThrough: filters.DateThrough ? filters.DateThrough : null
    };
  }

  const downloadReport = async () => {
    if (!validate()) return;
    downloadFileFromApiPost(
      "/report/GenerateCustomMenuItemsReport",
      buildPayload(),
      "CustomMenuItemsReport.xlsx"
    );
  };

  const executeReport = () => {
    if (!validate()) return;
    setIsLoading(true);
    post("report/CustomMenuItemsReport", buildPayload())
      .then((res) => {
        setData(res);
        setTainted(false);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="w-100 h-auto">
      <div className="flex justify-between">
        <PageHeader title="Custom Menu Items Report" />
        <div className="flex flex-row justify-between">
          <PrimaryButton
            disabled={!tainted}
            onClick={executeReport}
            text={"Run Report"}
            className="w-1/3 mr-2"
          />
          <PrimaryButton
            onClick={downloadReport}
            text={"Download Report"}
            className="w-1/3"
          />
        </div>
      </div>

      <div className="w-full h-1/4 justify-center">
        <ReportFilter
          setFilters={setFilters}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </div>

      {data && !tainted && !isLoading && (
        <ReportTable
          filters={filters}
          onFiltersChange={onFiltersChange}
          data={data}
        />
      )}
    </div>
  );
}
